// Keyword + column-name schema primitives.
//
// `KeywordSchema` validates that a Value is a `Keyword` instance. Use it
// in op schemas wherever an arg is a keyword Value (e.g. polymorphic `col`
// path segments, sort direction markers).
//
// `ColumnNameSchema` is the SSOT for "the way to name a column": a
// keyword (typical, e.g. `:revenue`) or a string (special-character
// fallback, e.g. `"col with spaces"`). Every op that names a column
// reuses this schema rather than inlining the union — inlining is the
// duplication this primitive exists to prevent. `columnNameString`
// normalises the union to a plain string for consumers.
//
// Two layers exist by design:
//
//   - **Value layer** (`ColumnNameSchema` / `ColumnName`): used for op
//     args that are evaluated to runtime Values before the schema sees
//     them (e.g. eager top-level ops whose args go through `evalNode`).
//   - **AST layer** (`ColumnNameAstSchema` / `ColumnNameAst`): used for
//     ops whose schema sees raw AST nodes — the agg-context funcs whose
//     parent verbs (`aggregate`, `time-bucket`, `pivot`) pass agg-call
//     subtrees verbatim through `compileAggPair` without recursive
//     evaluation. A keyword AST is `{$kw: string}`; a quoted-string
//     literal stays a `string` in the AST.
//
// Both layers normalise via `columnNameString` / `columnNameAstString`
// to a plain `string` so consumers downstream don't care which layer
// produced the name.

import { z } from 'zod';

import { isKeyword, type Keyword } from '../value.ts';

/** A `Keyword` Value (constructed by `:foo` parser tokens). The
 * `<col>` describe is the doc walker's type label — every op surface
 * accepting a Value-layer keyword names a column with it. */
export const KeywordSchema = z
  .custom<Keyword>((v: unknown): v is Keyword => isKeyword(v as Keyword), {
    message: 'expected a keyword (e.g. :name)',
  })
  .describe('<col>');

/**
 * A column name — keyword (typical) or string (special-character
 * fallback). The single source of truth for column-naming slots at the
 * **Value layer** (args pre-evaluated by the engine).
 */
export const ColumnNameSchema = z.union([KeywordSchema, z.string().min(1)]);

/** Type of a validated column name. */
export type ColumnName = z.infer<typeof ColumnNameSchema>;

/** Normalise a `ColumnName` to its plain string form. */
export const columnNameString = (name: ColumnName): string => (typeof name === 'string' ? name : name.name);

/**
 * A keyword **AST node** as the parser/normaliser produces it for
 * `:foo` tokens: `{ $kw: 'foo' }`. Used inside `ColumnNameAstSchema`
 * for ops whose schema sees raw AST (agg-context funcs).
 */
export const KeywordAstSchema = z
  .object({
    $kw: z.string().min(1),
  })
  .strict();

/**
 * A column name at the **AST layer** — a keyword AST node
 * (`{$kw: string}`) or a quoted-string literal. The SSOT for
 * "name a column inside a non-evaluated AST subtree" — agg-context
 * funcs use it because their parent verbs hand `compileAggPair` raw
 * agg-call subtrees verbatim, never routing them through `evalNode`
 * (so a Value-level `KeywordSchema` would never see a `Keyword`).
 */
export const ColumnNameAstSchema = z.union([KeywordAstSchema, z.string().min(1)]);

/** Type of a validated column-name AST arg. */
export type ColumnNameAst = z.infer<typeof ColumnNameAstSchema>;

/** Normalise a `ColumnNameAst` to its plain string form. */
export const columnNameAstString = (name: ColumnNameAst): string => (typeof name === 'string' ? name : name.$kw);

/**
 * Schema for an **enum-shaped kwarg at the AST layer** accepting EITHER
 * a keyword AST node (`:inner`) or a quoted-string literal (`"inner"`).
 * Both shapes canonicalise to the same plain string via
 * `enumValueAstString`. The LLM-friendly leniency policy: when an enum
 * kwarg's keyword and string forms canonicalise to the same value, the
 * engine accepts both rather than rejecting one as a "syntactic typo".
 *
 * Use anywhere an op declares a fixed value-set kwarg (`:type`,
 * `:fill`, `:period`, …). Reusing this factory keeps the leniency
 * uniform — agents can rely on the policy without per-op carve-outs.
 */
export const keywordOrEnumAstSchema = <const ValueType extends readonly [string, ...string[]]>(values: ValueType) =>
  z.union([
    KeywordAstSchema.refine((k): k is { $kw: ValueType[number] } => (values as readonly string[]).includes(k.$kw), {
      message: `expected one of: ${values.map((v) => `:${v}`).join(', ')}`,
    }),
    z.enum(values),
  ]);

/** Type of a validated `keywordOrEnumAstSchema(values)` value. */
export type KeywordOrEnumAst<ValueType extends string> = { readonly $kw: ValueType } | ValueType;

/** Normalise a `KeywordOrEnumAst` value to its plain string form. */
export const enumValueAstString = <ValueType extends string>(v: KeywordOrEnumAst<ValueType>): ValueType =>
  typeof v === 'string' ? v : v.$kw;
